import { Box, Container, Grid, SelectChangeEvent, Typography } from '@mui/material';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { validate } from '../../api/services/auth';
import { useNavigate } from 'react-router-dom';
import { pushNotification } from '../../components/PinnacleSnackbarProvider/PinnacleSnackbarProvider';
import { NotificationTypes } from '../../components/PinnacleSnackbarProvider/PinnacleSnackbarProvider.types';
import { handleApiError } from '../../utils/errorHandler';
import { ADMIN_STORAGE_KEY } from '../../utils/constants';
import Sidebar from '../../layouts/Sidebar/Sidebar';
import StatCard from './partials/StatCard/StatCard';
import { cardStatsConfig } from '../../utils/config';
import { dashboard } from '../../api/services/dashboard';
import { CardStat, GenericMostStat, NewUserStat } from '../../api/interfaces/Dashboard';
import { StatCardSkeleton } from '../../components/skeleton/StatCardSkeleton/StatCardSkeleton';
import { getPrograms } from '../../api/services/program';
import { Program } from '../../api/interfaces/Program';
import MostWatchedVideosCard from './partials/MostWatchedVideosCard/MostWatchedVideosCard';
import NewUsersCard from './partials/NewUsersCard/NewUsersCard';
import MostPopularReferenceCard from './partials/MostPopularReferenceCard/MostPopularReferenceCard';
import Header from '../../layouts/Header/Header';

type FilterDashboardParams = {
    videosProgramId?: string;
    referencesProgramId?: string;
    usersProgramId?: string;
    type?: string;
};

type SharedTooltipProps = {
    active?: boolean;
    payload?: any[];
    label?: string;
};

export const SharedTooltip = ({ active, payload }: SharedTooltipProps) => {
    if (active && payload && payload.length > 0) {
        const data = payload[0].payload;

        return (
            <Box sx={{ backgroundColor: '#fff', border: '1px solid #ccc', p: 1 }}>
                <Typography variant="body2" fontWeight={600}>
                    {data.title}
                </Typography>
                <Typography variant="caption">Views: {data.views}</Typography>
            </Box>
        );
    }

    return null;
};

const HomePage: React.FC = () => {
    const navigate = useNavigate();
    const [currentAdminName, setCurrentAdminName] = useState<string>('');
    const [isDashboardLoading, setIsDashboardLoading] = useState<boolean>(false);

    const [programs, setPrograms] = useState<Program[]>([]);

    const [overviewCardStats, setOverviewCardStats] = useState<CardStat[]>([]);

    const [overviewMostWatchedVideos, setOverviewMostWatchedVideos] = useState<GenericMostStat[]>([]);
    const [selectedVideosFilterProgram, setSelectedVideosFilterProgram] = useState<string>('');
    const [isMostWatchedVideosFilterLoading, setMostWatchedVideosFilterLoading] = useState<boolean>(false);

    const [overviewMostPopularReference, setOverviewMostPopularReference] = useState<GenericMostStat[]>([]);
    const [selectedReferencesFilterProgram, setSelectedReferencesFilterProgram] = useState<string>('');
    const [isMostPopularReferencesFilterLoading, setIsMostPopularReferencesFilterLoading] = useState<boolean>(false);

    const [overviewNewUsers, setOverviewNewUsers] = useState<NewUserStat[]>([]);
    const [selectedUsersFilterProgram, setSelectedUsersFilterProgram] = useState<string>('');
    const [isNewUsersFilterLoading, setIsNewUsersFilterLoading] = useState<boolean>(false);

    const cardStatsConfigMap = useMemo(() => {
        return cardStatsConfig.reduce((map, config) => {
            map[config.type] = config;
            return map;
        }, {} as Record<string, typeof cardStatsConfig[0]>);
    }, []);


    const barChartColors = ["#A82BFB", "#03B568", "#FA3D3D", "#FFDC00", "#FE6A35"]
    const getBarChartColor = (index: any) => barChartColors[index % barChartColors.length];

    const pieChartColors = ["#64BDC6", "#FA4B42", "#FE6A35", "#EECA34", "#2B72FB"]
    const getPieChartColor = (index: any) => pieChartColors[index % pieChartColors.length];


    const fetchDashboard = useCallback(async () => {
        try {
            const dashboardResponse = await dashboard();
            const cardStats = dashboardResponse.data.cardStats;
            const mergedCardStats = cardStats.map(stat => {
                const config = cardStatsConfigMap[stat.type];
                return {
                    ...stat,
                    ...config,
                };
            });
            setOverviewCardStats(mergedCardStats as any);

            const mostWatchedVideos = dashboardResponse.data.mostWatchedVideos;
            setOverviewMostWatchedVideos(mostWatchedVideos);

            const mostPopularReferences = dashboardResponse.data.mostPopularReferences;
            setOverviewMostPopularReference(mostPopularReferences);

            const newUsers = dashboardResponse.data.newUsers;
            setOverviewNewUsers(newUsers);
        } catch (error) {
            handleApiError(error, {
                notify: (msg) => {
                    pushNotification({
                        details: msg,
                        type: NotificationTypes.Error,
                    });
                },
            });
        }
    }, [cardStatsConfigMap]);

    const filterDashboard = useCallback(async (params: FilterDashboardParams) => {
        try {
            const { videosProgramId, referencesProgramId, usersProgramId, type } = params;
            const filter = {} as FilterDashboardParams;

            if (videosProgramId) {
                filter.videosProgramId = videosProgramId;
            } else if (referencesProgramId) {
                filter.referencesProgramId = referencesProgramId;
            } else if (usersProgramId) {
                filter.usersProgramId = usersProgramId;
            }
            const filterResponse = await dashboard(filter);


            if (videosProgramId) {
                const filteredMostWatchedVideos = filterResponse.data.mostWatchedVideos;
                setOverviewMostWatchedVideos(filteredMostWatchedVideos);
            } else if (referencesProgramId) {
                const filteredMostPopularReferences = filterResponse.data.mostPopularReferences;
                setOverviewMostPopularReference(filteredMostPopularReferences);
            } else if (usersProgramId) {
                const filteredNewUsers = filterResponse.data.newUsers;
                setOverviewNewUsers(filteredNewUsers);
            }

            if (!videosProgramId || !referencesProgramId || !usersProgramId) {
                if (type === 'videos') {
                    const filteredMostWatchedVideos = filterResponse.data.mostWatchedVideos;
                    setOverviewMostWatchedVideos(filteredMostWatchedVideos);
                } else if (type === 'references') {
                    const filteredMostPopularReferences = filterResponse.data.mostPopularReferences;
                    setOverviewMostPopularReference(filteredMostPopularReferences);
                } else if (type === 'users') {
                    const filteredNewUsers = filterResponse.data.newUsers;
                    setOverviewNewUsers(filteredNewUsers);
                }
            }

            return filterResponse;
        } catch (error) {
            handleApiError(error, {
                notify: (msg) => {
                    pushNotification({
                        details: msg,
                        type: NotificationTypes.Error,
                    });
                },
            });
        } finally {
            setMostWatchedVideosFilterLoading(false);
            setIsMostPopularReferencesFilterLoading(false);
            setIsNewUsersFilterLoading(false);
        }
    }, []);

    const fetchPrograms = useCallback(async () => {
        try {
            const programResponse = await getPrograms();
            setPrograms(programResponse.data);
        } catch (error) {
            handleApiError(error, {
                notify: (msg) => {
                    pushNotification({
                        details: msg,
                        type: NotificationTypes.Error,
                    });
                },
            });
        }
    }, []);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        const run = async () => {
            const token =
                localStorage.getItem(ADMIN_STORAGE_KEY) ||
                sessionStorage.getItem(ADMIN_STORAGE_KEY);
            if (!token) {
                navigate('/login');
                return;
            }
            try {
                setIsDashboardLoading(true);
                const validateResponse = await validate();
                setCurrentAdminName(validateResponse.data.firstName);
                await fetchPrograms();
                await fetchDashboard();
            } catch (error) {
                handleApiError(error, {
                    notify: (msg) => {
                        pushNotification({
                            details: msg,
                            type: NotificationTypes.Error,
                        });
                    },
                });
            } finally {
                setIsDashboardLoading(false);
                setMostWatchedVideosFilterLoading(false);
                setIsMostPopularReferencesFilterLoading(false);
                setIsNewUsersFilterLoading(false);
            }
        };
        run();
    }, [fetchDashboard, fetchPrograms, navigate]);

    const handleVideosFilterByProgramChange = async (event: SelectChangeEvent<string>) => {
        setSelectedVideosFilterProgram(event.target.value);
        setMostWatchedVideosFilterLoading(true);
        await filterDashboard({
            type: 'videos',
            videosProgramId: event.target.value
        });
    };

    const handleReferencesFilterByProgramChange = async (event: SelectChangeEvent<string>) => {
        setSelectedReferencesFilterProgram(event.target.value);
        setIsMostPopularReferencesFilterLoading(true);
        await filterDashboard({
            type: 'references',
            referencesProgramId: event.target.value
        });
    };

    const handleUsersFilterByProgramChange = async (event: SelectChangeEvent<string>) => {
        setSelectedUsersFilterProgram(event.target.value);
        setIsNewUsersFilterLoading(true);
        await filterDashboard({
            type: 'users',
            usersProgramId: event.target.value
        });
    };

    const chartBoxStyles = { flex: 1, minWidth: 300, height: 300, pt: 5 };

    const handleRedirect = (type: string) => {
        navigate(type);
    }

    return (

        <main style={{ display: 'flex', width: '100%', minHeight: '100vh', backgroundColor: '#f6f6f6' }}>
            <Sidebar />

            <Box
                component="section"
                sx={{
                    flexGrow: 1,
                    width: '100%',
                    overflowX: 'hidden'
                }}
            >
                <Container
                    maxWidth="lg"
                    sx={{
                        mt: { xs: 4, sm: 6 },
                        mb: 4,
                        px: { xs: 2, sm: 3 },
                    }}
                >
                    <Header title='Overview' currentAdminName={currentAdminName} />

                    <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 2, md: 3 }}>
                        {isDashboardLoading ? (
                            Array.from({ length: 4 }).map((_, idx) => (
                                <Grid size={3} key={idx}>
                                    <StatCardSkeleton />
                                </Grid>
                            ))
                        ) : (
                            overviewCardStats.map(({ title, count, countAddedLastWeek, icon: Icon, bgColor, textColor, type }) => (
                                <Grid size={3}>
                                    <StatCard
                                        title={title}
                                        subtitle={
                                            countAddedLastWeek > 0
                                                ? `${countAddedLastWeek} new ${type} added last week.`
                                                : 'No new additions last week.'
                                        }
                                        value={String(count)}
                                        icon={<Icon />}
                                        bgColor={bgColor}
                                        textColor={textColor}
                                        onClick={() => handleRedirect(type)}
                                    />
                                </Grid>
                            ))
                        )}
                    </Grid>

                    <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 2, md: 3 }}>
                        <Grid size={7}>
                            <Box sx={chartBoxStyles}>
                                <MostWatchedVideosCard
                                    isDashboardLoading={isDashboardLoading}
                                    isMostWatchedVideosFilterLoading={isMostWatchedVideosFilterLoading}
                                    selectedVideosFilterProgram={selectedVideosFilterProgram}
                                    handleVideosFilterByProgramChange={handleVideosFilterByProgramChange}
                                    programs={programs}
                                    overviewMostWatchedVideos={overviewMostWatchedVideos}
                                    getBarChartColor={getBarChartColor}
                                />
                            </Box>

                            <Box sx={{ pt: '150px' }}>
                                <NewUsersCard
                                    isDashboardLoading={isDashboardLoading}
                                    isNewUsersFilterLoading={isNewUsersFilterLoading}
                                    selectedUsersFilterProgram={selectedUsersFilterProgram}
                                    handleUsersFilterByProgramChange={handleUsersFilterByProgramChange}
                                    programs={programs}
                                    overviewNewUsers={overviewNewUsers}
                                />
                            </Box>
                        </Grid>
                        <Grid size={5} sx={{ pt: '40px' }}>
                            <MostPopularReferenceCard
                                isDashboardLoading={isDashboardLoading}
                                isMostPopularReferencesFilterLoading={isMostPopularReferencesFilterLoading}
                                selectedReferencesFilterProgram={selectedReferencesFilterProgram}
                                handleReferencesFilterByProgramChange={handleReferencesFilterByProgramChange}
                                programs={programs}
                                overviewMostPopularReference={overviewMostPopularReference}
                                getPieChartColor={getPieChartColor}
                            />
                        </Grid>
                    </Grid>
                </Container>
            </Box>
        </main>
    );
};

export default HomePage;
